import { FilterOutlined } from '@ant-design/icons';
import { Table, Typography, Space, Button, Pagination, Popover, Radio, Row, Col, Input } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio';
import { ColumnsType } from 'antd/lib/table';
import { format } from 'date-fns';
import React from 'react';
import '@/GachaWinning/styles/gachaWinningTable.css';

import { GachaWinning } from '@/api-client/generated/models';

type GachaWinningApproveTableProps = {
  gachaWinnings: GachaWinning[];
  gachaWinningsNum: number;
  currentPage: number;
  perPage: number;
  onChangeCurrentPage: (newPage: number) => Promise<void>;
  onChangeOs: (os: string) => Promise<void>;
  onSearch: (word: string) => void;
};

type TableHeaderProps = {
  totalNum: number;
  onSearch: (word: string) => void;
};

type OsHeaderProps = {
  // eslint-disable-next-line react/require-default-props
  onChangeOsRadio?: (e: RadioChangeEvent) => void;
};

const dateFormat = 'yyyy/MM/dd HH:mm:ss';

const isSmsAuthed = (smsAuthedAt: Date) => smsAuthedAt > new Date(2019, 0); // 0値でいつSMS認証したかが入ってきてしまうため、サービスインした2020の前の年以降にSMS認証をしたかで比較

const OsHeader: React.FC<OsHeaderProps> = ({ onChangeOsRadio }) => {
  const content = (
    <Radio.Group className="os-select" onChange={onChangeOsRadio} defaultValue="">
      <Radio value="" className="os-select-el">
        全てのOS
      </Radio>
      <Radio value="ios" className="os-select-el">
        iOS
      </Radio>
      <Radio value="android" className="os-select-el">
        Android
      </Radio>
    </Radio.Group>
  );
  return (
    <Space>
      OS
      <Popover content={content} trigger="focus" placement="bottom">
        <Button type="text">
          <FilterOutlined />
        </Button>
      </Popover>
    </Space>
  );
};

const TableHeader: React.FC<TableHeaderProps> = ({ totalNum, onSearch }) => (
  <Row>
    <Col span={2}>
      <Typography.Title level={5}>総数: {totalNum || 0}</Typography.Title>
    </Col>
    <Col span={6}>
      <Input.Search placeholder="フリーワード検索" onSearch={onSearch} />
    </Col>
  </Row>
);

const GachaWinningTable: React.FC<GachaWinningApproveTableProps> = ({
  gachaWinnings,
  gachaWinningsNum,
  currentPage,
  perPage,
  onChangeCurrentPage,
  onChangeOs,
  onSearch,
}) => {
  const onChangeOsRadio = (e: RadioChangeEvent) => {
    onChangeOs(e.target.value);
  };

  const onChangeCurrentPagePagination = (page: number) => {
    onChangeCurrentPage(page);
  };

  const giftCodeStatusValue = {
    unauthed: 'SMS 認証待ち',
    unapproved: '承認待ち',
    gifted: '配信済み',
    rejected: '棄却済み',
  };

  const columns: ColumnsType = [
    {
      title: 'ユーザ名',
      dataIndex: ['user', 'nickname'],
      key: 'username',
    },
    {
      title: 'メールアドレス',
      dataIndex: ['user', 'mailAddress'],
      key: 'mailAddress',
    },
    {
      title: OsHeader({ onChangeOsRadio }),
      dataIndex: 'os',
      key: 'os',
    },
    {
      title: '当選日時',
      key: 'createdAt',
      render: (_: any, row: GachaWinning) => format(row.createdAt, dateFormat),
    },
    {
      title: 'ギフトコード',
      key: 'giftCode',
      dataIndex: ['giftCode', 'code'],
    },
    {
      title: '配信したか',
      key: 'status',
      render: (_: any, row: GachaWinning) =>
        (!!row.user.smsAuthedAt && isSmsAuthed(row.user.smsAuthedAt) // FIXME: https://github.com/voyagegroup/lighthouse-studio/issues/2195
          ? giftCodeStatusValue[row.status]
          : giftCodeStatusValue.unauthed) || 'エラー',
    },
  ];

  return (
    <div>
      <Table
        columns={columns}
        dataSource={gachaWinnings}
        rowKey={(gachaWinning: GachaWinning) => gachaWinning.id}
        pagination={false}
        title={() => TableHeader({ totalNum: gachaWinningsNum, onSearch })}
      />
      <Pagination
        simple
        current={currentPage}
        pageSize={perPage}
        total={gachaWinningsNum}
        onChange={onChangeCurrentPagePagination}
      />
    </div>
  );
};

export default GachaWinningTable;
